"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";

export interface TooltipProps {
  content: string | React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  /** Drop the tooltip's own panel chrome when `content` already renders a card/panel (avoids double container). */
  bare?: boolean;
  className?: never;
}

export function Tooltip({
  content,
  children,
  side = "top",
  bare = false,
  className,
}: TooltipProps) {
  const [show, setShow] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      // ponytail: snapshot trigger rect so the portaled tooltip can position itself fixed
      setRect(wrapperRef.current?.getBoundingClientRect() ?? null);
      setShow(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShow(false);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Fixed-position the portaled panel relative to the trigger rect, offset 8px on the chosen side.
  const positionStyle = (r: DOMRect): React.CSSProperties => {
    switch (side) {
      case "bottom":
        return { top: r.bottom + 8, left: r.left + r.width / 2, transform: "translateX(-50%)" };
      case "left":
        return { top: r.top + r.height / 2, left: r.left - 8, transform: "translate(-100%, -50%)" };
      case "right":
        return { top: r.top + r.height / 2, left: r.right + 8, transform: "translateY(-50%)" };
      case "top":
      default:
        return { top: r.top - 8, left: r.left + r.width / 2, transform: "translate(-50%, -100%)" };
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {show &&
        rect &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            className={cn(
              "pointer-events-none fixed w-max",
              bare
                ? ""
                : "max-w-xs rounded-xl border border-slate-200/70 bg-white px-3 py-2 text-xs text-slate-700 shadow-[0_6px_28px_-16px_rgba(15,23,42,0.35)] dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300",
              "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95",
              className
            )}
            style={{ ...positionStyle(rect), zIndex: Z_INDEX.TOOLTIP }}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
}
